import { AfterViewInit, Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { NgForm } from '@angular/forms';
import {
    DropdownWidget,
    FileListSpecificationModel,
    FileSpecificationModel,
    FileType,
    FileTypeCategory,
    InputOutputType,
    ModuleDataType,
    ModulePropertyOptionItem,
    RadioButtonGroupWidget,
    SingleChoiceSpecificationModel,
    SpecificationPropertyObject,
    StringResource,
    UiControlType,
} from '@ca/core/models';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { OptionItem } from 'src/app/core/models/option-item.model';
import { fileTypeCategories, moduleControlTypes, moduleDataTypes, moduleFileTypes } from '../../../constants/input-types.constants';
import { ModuleSpecService } from '../module-spec.service';

@UntilDestroy()
@Component({
    // new because old one still exist plz rename after
    selector: 'ca-module-spec-editor-new',
    templateUrl: './module-spec-editor.component.html',
    styleUrls: ['./module-spec-editor.component.scss'],
})
export class ModuleSpecEditorComponent implements OnInit, AfterViewInit {
    get inputDataTypes(): Array<ModulePropertyOptionItem> {
        return moduleDataTypes;
    }

    get outputDataTypes(): Array<ModulePropertyOptionItem> {
        return moduleDataTypes.filter((option: ModulePropertyOptionItem) => !option.inputOnly);
    }

    get isSpecTypeFile(): boolean {
        let isFileType: boolean = false;
        if (this.activeProperty != null) {
            isFileType = this.activeProperty.specType === 'file' || this.activeProperty.specType === 'list[file]';
        }

        return isFileType;
    }

    get uiControlTypes(): Array<OptionItem<UiControlType>> {
        let shownWidgets: Array<OptionItem<UiControlType>> = [];
        if (this.activeProperty && this.activeProperty.specType) {
            const widgetType: ModulePropertyOptionItem | undefined = moduleDataTypes.find(
                (type: ModulePropertyOptionItem) => type.value === this.activeProperty!.specType,
            );

            if (widgetType != null) {
                shownWidgets = moduleControlTypes.filter((controlOption: OptionItem<UiControlType>) => widgetType.widgets.includes(controlOption.value));
            }

            shownWidgets = shownWidgets.sort((a, b) => (a.label > b.label ? 1 : -1));
        }

        return shownWidgets;
    }

    get dataTypesToShow(): Array<ModulePropertyOptionItem> {
        let dataToShow: Array<ModulePropertyOptionItem> = [];
        if (this.activeProperty != null) {
            dataToShow = this.activeProperty.type === InputOutputType.Input ? this.inputDataTypes : this.outputDataTypes;
        }
        return dataToShow;
    }

    get hasActiveProperty(): boolean {
        return this.activeProperty != null;
    }

    get isActivePropertyInput(): boolean {
        return this.activeProperty != null && this.activeProperty.type === InputOutputType.Input ? true : false;
    }

    get activeProperty(): SpecificationPropertyObject | undefined {
        return this.moduleSpecService.getActiveProperty();
    }

    get activePropertyTypeLabel(): string | undefined {
        let label: string = 'Input';
        if (this.activeProperty != null && this.activeProperty.type === InputOutputType.Output) {
            label = 'Output';
        }

        return label;
    }

    /**
     * Checks if all files are selected in the specification. Checks if a string of "*" is found
     * in the format array or string
     */
    get isAllFilesChecked(): boolean {
        return this.isFormatSelectedInSpecification(this.allFilesFormatString);
    }

    /**
     * This map is a 1 to 1 representation of the (selected) file formats in the specification (that's why it has only a getter
     * and not setter). We never change any value in this map, we always relalculate according to what the
     * state of the specification is.
     */
    get supportedFileTypesOfSelectedCategoriesMap(): Map<string, boolean> {
        const supportedFileTypesMap: Map<string, boolean> = new Map<string, boolean>();
        moduleFileTypes.map((fileTypeObj: FileType) => {
            const isCategoryChecked: boolean | undefined = this.fileCategoriesMap.get(fileTypeObj.category);

            // If the category of the file is checked, AND the format is not in the map yet, add it.
            if (isCategoryChecked === true && !supportedFileTypesMap.has(fileTypeObj.format)) {
                const checkboxVal: boolean = this.isFormatSelectedInSpecification(fileTypeObj.format);
                supportedFileTypesMap.set(fileTypeObj.format, checkboxVal);
            } else if (isCategoryChecked === false && supportedFileTypesMap.has(fileTypeObj.format)) {
                // If the category is NOT checked, but the file format is found in the Map, delete it.
                supportedFileTypesMap.delete(fileTypeObj.format);
            }
        });

        return supportedFileTypesMap;
    }

    get internetAccess(): boolean | undefined {
        return this.moduleSpecService.getInternetAccess();
    }
    set internetAccess(val: boolean | undefined) {
        this.moduleSpecService.setInternetAccess(val || false);
    }
    get gpuUsage(): boolean | undefined {
        return this.moduleSpecService.getGpuUsage();
    }
    set gpuUsage(val: boolean | undefined) {
        this.moduleSpecService.setGpuUsage(val || false);
    }
    @ViewChild('form') form: NgForm;

    @Output() onFormValidityChange = new EventEmitter<boolean>();
    @Input() propertyDefinitionComplete: boolean;

    fileCategoriesMap: Map<FileTypeCategory, boolean> = new Map<FileTypeCategory, boolean>();

    private readonly fileCategories: FileTypeCategory[] = fileTypeCategories;
    private readonly allFilesFormatString: string = '*';
    private isUiWidgetDemoFormValid: boolean = true;

    constructor(private moduleSpecService: ModuleSpecService) {}

    ngOnInit(): void {
        // Everytime the active property changes, we need to recalculate the checked state of the file categories
        this.moduleSpecService.activePropertyChanged$.pipe(untilDestroyed(this)).subscribe(() => {
            this.initFileCategoriesMap();
        });
    }

    ngAfterViewInit(): void {
        if (this.form) {
            this.form.statusChanges?.subscribe((status: 'VALID' | 'INVALID') => {
                const areBothFormsValid = status === 'VALID' && this.isUiDemoWidgetShownAndValid();
                this.onFormValidityChange.emit(areBothFormsValid);
            });
        }
    }

    handleWidgetDemoFormValidity(isWidgetDemoFormValid: boolean): void {
        this.isUiWidgetDemoFormValid = isWidgetDemoFormValid;
        const areBothFormsValid = (this.form.valid || false) && this.isUiDemoWidgetShownAndValid();
        this.onFormValidityChange.emit(areBothFormsValid);
    }

    updateDescription(description: string): void {
        if (this.activeProperty != null && this.activeProperty.uiProperty != null) {
            this.activeProperty.uiProperty.description = description;
        }
    }

    /**
     * When we change a Label of an item, we must then adjust the key accordingly.
     * Afterwards, we should synchronize with the spec property, because this takes all keys
     * and puts them into the spec property
     */
    adjustKeyToLabel(item: StringResource): void {
        if (item.label != null && item.label !== '') {
            let newKey: string = item.label.toLowerCase();

            // replace all occurences of a string
            newKey = newKey.split(' ').join('_');
            item.key = newKey;

            this.synchronizeDropdownRadioItemsWithSpecProp();
        }
    }

    getDropdownOrRadioItems(): StringResource[] {
        let items: StringResource[] = [];
        if (this.activeProperty && (this.activeProperty.widgetSpecificUiProperty as RadioButtonGroupWidget | DropdownWidget).items) {
            items = (this.activeProperty.widgetSpecificUiProperty as RadioButtonGroupWidget | DropdownWidget).items;
        }
        return items;
    }

    checkOrUncheckAllFiles(checked: boolean): void {
        this.clearAllFormatsInSpec();
        if (checked === true) {
            this.addFileFormatToSpecification(this.allFilesFormatString);
        }
    }

    checkOrUncheckFileCategory(checked: boolean, category: FileTypeCategory): void {
        this.fileCategoriesMap.set(category, checked);

        // If the category got unchecked, we need to also uncheck all its formats.
        if (checked === false && this.isAnyFormatSelectedFromCategory(category)) {
            const allFormatsFromCategory: FileType[] = moduleFileTypes.filter((ft: FileType) => ft.category === category);

            allFormatsFromCategory.map((fileType: FileType) => {
                if (this.isFormatSelectedInSpecification(fileType.format) === true) {
                    this.checkOrUncheckFileFormat(false, fileType.format);
                }
            });
        }
    }

    checkOrUncheckFileFormat(checked: boolean, format: string): void {
        if (checked === true) {
            this.addFileFormatToSpecification(format);
        } else {
            this.removeFileFormatFromSpecification(format);
        }
    }

    setDefaultOption(label: string): void {
        if (this.activeProperty != null) {
            this.activeProperty.specProperty.default = label;
        }
    }

    addItem(): void {
        if (this.activeProperty != null) {
            const itemsArr: Array<StringResource> | undefined = (this.activeProperty.widgetSpecificUiProperty as DropdownWidget | RadioButtonGroupWidget).items;
            const nextCount: number = itemsArr.length + 1;
            const name: string = 'Item ' + nextCount;
            const key: string = 'item_' + nextCount;

            itemsArr.push({ key, label: name });

            this.synchronizeDropdownRadioItemsWithSpecProp();
            this.resetDefaultValue();
        }
    }

    deleteItem(index: number): void {
        if (this.activeProperty != null) {
            const itemsArr: Array<StringResource> | undefined = (this.activeProperty.widgetSpecificUiProperty as DropdownWidget | RadioButtonGroupWidget).items;
            if (itemsArr != null && itemsArr[index] != null) {
                itemsArr.splice(index, 1);

                this.synchronizeDropdownRadioItemsWithSpecProp();
                this.resetDefaultValue();
            }
        }
    }

    /**
     * Method which handes changing the Input/Output type dropdown entry and writes it into the spec
     */
    changeDataType(dataType: ModuleDataType): void {
        if (this.activeProperty != null) {
            const didDataTypeChange: boolean = this.activeProperty.specType !== dataType;
            this.activeProperty.changeSpecTypeTo(dataType);

            // Always when we change the Data Type (to another value), we also have to reset the uiControlType
            // (=preselect the first one)
            if (didDataTypeChange === true) {
                // Only for Inputs (Outputs don't have a UI control type):
                // If there is any uiControlTypes, preselect the first one. If there's none, set to undefined.
                if (this.activeProperty.type === InputOutputType.Input && this.uiControlTypes.length > 0) {
                    const firstUiControlTypeKey: UiControlType = this.uiControlTypes[0].value;
                    this.changeUiControlType(firstUiControlTypeKey);
                }

                // If Input/Output type is changed to file or file_list, we need to initialize the file categories
                if (this.isSpecTypeFile) {
                    this.initFileCategoriesMap();
                }
            }
        }
    }

    changeUiControlType(uiControlType: UiControlType): void {
        if (this.activeProperty != null) {
            this.activeProperty.changeUiControlTypeTo(uiControlType);
        }
    }

    shouldShowUiDemoControl(): boolean {
        return (this.activeProperty?.uiControlType && this.activeProperty.uiControlType !== 'none' && this.isActivePropertyInput) || false;
    }

    updateLabelAndKeyInSpec(newLabel: string): void {
        this.moduleSpecService.updateLabel(newLabel);
    }

    /**
     * If UiDemoWidgetComponent is not shown, we return true (because it shouln't make the form invalid)
     * If it's shown, we check if it's valid.
     */
    private isUiDemoWidgetShownAndValid(): boolean {
        return this.shouldShowUiDemoControl() ? this.isUiWidgetDemoFormValid : true;
    }

    /**
     * For a Dropdown and a RadioButtonGroup we have to keep widgetSpecificUiProperty.items in sync with widgetSpecificSpecProperty
     */
    private synchronizeDropdownRadioItemsWithSpecProp(): void {
        if (this.activeProperty != null && this.activeProperty.widgetSpecificSpecProperty != null) {
            if (this.activeProperty.uiControlType === 'dropdown' || this.activeProperty.uiControlType === 'radiobuttongroup') {
                const keysToAdd: string[] = (this.activeProperty.widgetSpecificUiProperty as DropdownWidget).items.map((item: StringResource) => item.key);

                // First, empty the array
                (this.activeProperty.widgetSpecificSpecProperty as SingleChoiceSpecificationModel).length = 0;

                // Now add the collection of items.
                (this.activeProperty.widgetSpecificSpecProperty as SingleChoiceSpecificationModel).push(...keysToAdd);
            }
        }
    }

    /**
     * Determines the checked state of the file categories (image, data, other). Since this state is
     * not saved in the database, we calculate it according to which formats are checked. Those checked formats'
     * categories will be checked then as well.
     */
    private initFileCategoriesMap(): void {
        const fileCategoriesMap: Map<FileTypeCategory, boolean> = new Map<FileTypeCategory, boolean>();
        if (this.isSpecTypeFile) {
            this.fileCategories.map((cat: FileTypeCategory) => {
                // Only show categories where at least one format exists
                const anyFormatExistsForCategory: boolean = moduleFileTypes.some((ft: FileType) => ft.category === cat);

                if (anyFormatExistsForCategory === true) {
                    const isChecked: boolean = this.isAnyFormatSelectedFromCategory(cat);
                    fileCategoriesMap.set(cat, isChecked);
                }
            });
        }

        this.fileCategoriesMap = fileCategoriesMap;
    }

    private removeFileFormatFromSpecification(format: string): void {
        if (this.activeProperty && this.isSpecTypeFile === true) {
            let selectedFormatsInSpec: string | string[] | undefined = (
                this.activeProperty!.widgetSpecificSpecProperty as FileSpecificationModel | FileListSpecificationModel
            ).format;

            selectedFormatsInSpec = this.parseFormatPropToArray(selectedFormatsInSpec);
            const indexOfFormat: number | undefined = selectedFormatsInSpec.findIndex((formatIterator: string) => formatIterator === format);

            selectedFormatsInSpec.splice(indexOfFormat, 1);

            // Re-add the array (since in some cases it's not a reference, but a primitive (i.e. if it was a string))
            (this.activeProperty!.widgetSpecificSpecProperty as FileSpecificationModel | FileListSpecificationModel).format = selectedFormatsInSpec;
        }
    }

    /**
     * The value of the format property (in the spec['type:file'] || spec['type:list[file]'] property) can
     * come as a string, a string[] or undefined.
     * This method unifies them -> in the end it returns an array.
     */
    private parseFormatPropToArray(selectedFormatsInSpec: string | string[] | undefined): string[] {
        if (selectedFormatsInSpec == null) {
            selectedFormatsInSpec = [];
        } else if (typeof selectedFormatsInSpec === 'string') {
            // If it's a string, convert into an array and add
            // Otherwise it's an array already, and we can just push the new format
            selectedFormatsInSpec = [selectedFormatsInSpec];
        }

        return [...selectedFormatsInSpec];
    }

    /**
     * Checks if the given {format} is selected and listed in the spec
     * for the activeProperty
     */
    private isFormatSelectedInSpecification(format: string): boolean {
        let isChecked: boolean = false;
        if (this.isSpecTypeFile && this.activeProperty) {
            const formatPropFromSpecification: string | Array<string> | undefined = (
                this.activeProperty!.widgetSpecificSpecProperty as FileSpecificationModel | FileListSpecificationModel
            ).format;

            if (formatPropFromSpecification != null) {
                // if format is a string
                if (!Array.isArray(formatPropFromSpecification) && formatPropFromSpecification === format) {
                    isChecked = true;
                } else if (Array.isArray(formatPropFromSpecification)) {
                    isChecked = formatPropFromSpecification.includes(format);
                }
            }
        }

        return isChecked;
    }

    private clearAllFormatsInSpec(): void {
        if (this.activeProperty && this.isSpecTypeFile === true) {
            (this.activeProperty!.widgetSpecificSpecProperty as FileSpecificationModel | FileListSpecificationModel).format = [];

            // Afterwards we need to re-initialize (=recalculate) checked categories, because they changed
            // (since we deleted all file formats)
            this.initFileCategoriesMap();
        }
    }

    /**
     * This method sets new formats into the specification.
     * ATTENTION: We cannot change the value in the Map (supportedFileTypesOfSelectedCategoriesMap),
     * because the Map is just a getter. It always gets the current values out of the spec.
     */
    private addFileFormatToSpecification(format: string): void {
        if (this.activeProperty && this.isSpecTypeFile === true) {
            let selectedFormatsInSpec: string | string[] | undefined = (
                this.activeProperty!.widgetSpecificSpecProperty as FileSpecificationModel | FileListSpecificationModel
            ).format;

            selectedFormatsInSpec = this.parseFormatPropToArray(selectedFormatsInSpec);
            selectedFormatsInSpec.push(format);

            // Re-add the array (since in some cases it's not a reference, but a primitive (i.e. if it was a string))
            (this.activeProperty!.widgetSpecificSpecProperty as FileSpecificationModel | FileListSpecificationModel).format = selectedFormatsInSpec;
        }
    }

    /**
     * Resets the "default" property in SpecProperty
     */
    private resetDefaultValue(): void {
        if (this.activeProperty != null) {
            this.activeProperty.specProperty.default = undefined;
        }
    }

    /**
     * Checks all file types from a {category} and returns true if any is selected (in spec)
     *
     * @param category - the category to check all files
     *
     * @returns true if any file type is selected from the given {category}, otherwise false
     */
    private isAnyFormatSelectedFromCategory(category: FileTypeCategory): boolean {
        const fileTypesFromCategory: Array<FileType> = moduleFileTypes.filter((type: FileType) => type.category === category);
        return fileTypesFromCategory.some((fileType: FileType) => this.isFormatSelectedInSpecification(fileType.format));
    }
}
